/**
 * MembershipTemplateAdmin — the list for Admin > Memberships and Admin >
 * Packages: the corporate dashboard's membership-template table
 * (components/memberships-table.tsx) for one account — corp's rows without the
 * cross-account title grouping — in the Lead Sources layout (AdminList.tsx):
 *   - Title (image, and for a package its availability: Promo, dates, who can
 *     buy, limits), Has Image, Available in Store, Description, Amount, Items
 *     (product and procedure counts, listed on click) and, for packages,
 *     Categories; by title;
 *   - search by title or description (debounced, as corp); packages filter by
 *     Type (Standard / Promo); both filter by store availability, as Products
 *     does (SCRUM-2438); Show deleted;
 *   - "Add membership" / "Add package" top right; a click on a live row opens
 *     it; Edit / Delete on a live row, Restore on a deleted one.
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live row still opens, read-only (MembershipTemplateForm).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ImageIcon, Package, Plus, Store, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { useDebounce } from '@/hooks/useDebounce';
import { cn, descriptionToText } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  deleteTemplate,
  listTemplates,
  restoreTemplate,
  type PackageAvailability,
  type TemplateKind,
  type TemplateListItem,
} from '@/services/membershipTemplates.service';
import { toast } from '@/store/toastStore';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from './AdminList';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from './AdminRowActions';
import { CountPill, FlagIcon, OutlineBadge } from './CorpBadges';
import { ListFilterBar, anyFilterActive, type ListFilterConfig } from './ListFilterBar';
import { MembershipTemplateForm } from './MembershipTemplateForm';
import { STORE_AVAILABILITY_OPTIONS, matchesStoreAvailability } from './storeAvailability';
import { useListPagination } from './useListPagination';
import { compareNumber, compareText, useListSort, type ListSortCompare } from './useListSort';

const SEARCH_DEBOUNCE_MS = 400;

const TEXT: Record<TemplateKind, { title: string; subtitle: string; noun: [string, string]; Noun: string }> = {
  membership: {
    title: 'Memberships',
    subtitle: 'Manage membership plans across all facilities',
    noun: ['membership', 'memberships'],
    Noun: 'Membership',
  },
  package: {
    title: 'Packages',
    subtitle: 'Manage service packages and bundled offerings',
    noun: ['package', 'packages'],
    Noun: 'Package',
  },
};

function columnsFor(kind: TemplateKind, locked: boolean): AdminListColumn[] {
  return [
    { key: 'title', header: 'Title', sortable: true },
    {
      key: 'hasImage',
      header: <ImageIcon className="mx-auto h-3.5 w-3.5 text-slate-400" aria-label="Has Image" />,
      align: 'center',
      className: 'w-12 px-1',
    },
    {
      key: 'inStore',
      header: <Store className="mx-auto h-3.5 w-3.5 text-slate-400" aria-label="Available in Store" />,
      align: 'center',
      className: 'w-12 px-1',
    },
    { key: 'description', header: 'Description' },
    { key: 'amount', header: 'Amount', sortable: true, className: 'w-28' },
    { key: 'items', header: 'Items', className: 'w-32' },
    ...(kind === 'package' ? [{ key: 'categories', header: 'Categories', className: 'w-48' }] : []),
    // A Locked page has no row actions.
    ...(locked ? [] : [ACTIONS_COLUMN]),
  ];
}

function templateTitle(t: TemplateListItem): string {
  return t.title.trim() || `#${t.membershipTemplateID}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function shortDate(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : '';
}

/** Corp's PackageAvailabilityLine text: dates, who, limits. */
export function availabilityParts(a: PackageAvailability): string[] {
  const parts: string[] = [];
  parts.push(a.type === 'date-range' && a.startDate ? `${shortDate(a.startDate)} – ${a.stopDate ? shortDate(a.stopDate) : '∞'}` : 'No expiration');
  if (a.availableTo === 1) parts.push('Non-members only');
  if (a.availableTo === 2) parts.push('Members only');
  if (a.maxUses > 0) parts.push(`Max ${a.maxUses}`);
  if (a.maxPatient > 0) parts.push(`${a.maxPatient} per patient`);
  return parts;
}

function ItemsPill({ tone, icon, items, heading, label }: { tone: 'blue' | 'purple'; icon: ReactNode; items: { name: string; quantity: number }[]; heading: string; label: string }) {
  if (items.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <CountPill tone={tone} icon={icon} count={items.length} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold text-slate-600">{heading}</p>
        <ul className="space-y-1 text-sm text-slate-700">
          {items.map((i, n) => (
            <li key={`${i.name}-${n}`}>
              {i.name} <span className="text-slate-400">×{i.quantity}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export function MembershipTemplateAdmin({ kind }: { kind: TemplateKind }) {
  const text = TEXT[kind];
  const locked = useAdminPageLevel() === 'locked';
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [store, setStore] = useState<string[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [deleting, setDeleting] = useState<TemplateListItem | null>(null);
  const debounced = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        setTemplates(await listTemplates(kind));
      } catch (err) {
        toast.error(getErrorMessage(err, `Failed to load ${text.noun[1]}`));
      } finally {
        setLoading(false);
      }
    },
    [kind, text.noun],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const filters: ListFilterConfig[] = [
    ...(kind === 'package'
      ? [
          {
            id: 'packages-type-filter',
            label: 'Filter by type',
            placeholder: 'All types',
            options: [
              { value: '0', label: 'Standard' },
              { value: '1', label: 'Promo' },
            ],
            selected: types,
            onChange: setTypes,
            widthClass: 'w-40',
          },
        ]
      : []),
    {
      id: `${text.noun[1]}-store-filter`,
      label: 'Filter by store availability',
      placeholder: 'All store availability',
      options: STORE_AVAILABILITY_OPTIONS,
      selected: store,
      onChange: setStore,
    },
  ];
  const filtering = anyFilterActive(filters);

  const query = debounced.trim().toLowerCase();
  const rows = useMemo(() => {
    const typeSet = new Set(types);
    return templates
      .filter((t) => showDeleted || t.active)
      .filter((t) => typeSet.size === 0 || typeSet.has(String(t.availability?.packageType ?? 0)))
      .filter((t) => matchesStoreAvailability(store, t.publicStatus))
      .filter((t) => query === '' || t.title.toLowerCase().includes(query) || descriptionToText(t.description).toLowerCase().includes(query))
      .sort((a, b) => compareText(templateTitle(a), templateTitle(b)) || a.membershipTemplateID - b.membershipTemplateID);
  }, [templates, showDeleted, types, store, query]);

  const sortCompare: ListSortCompare<TemplateListItem> = {
    title: (a, b) => compareText(templateTitle(a), templateTitle(b)),
    amount: (a, b) => compareNumber(a.membershipTotal, b.membershipTotal),
  };
  const sorting = useListSort(rows, { initialKey: 'title', compare: sortCompare });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted, types, store] });

  async function handleDelete() {
    if (!deleting) return;
    const id = deleting.membershipTemplateID;
    try {
      await deleteTemplate(kind, id);
      setTemplates((prev) => prev.map((t) => (t.membershipTemplateID === id ? { ...t, active: false } : t)));
      toast.success(`${text.Noun} deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, `The ${text.noun[0]} could not be deleted.`));
    }
  }

  async function handleRestore(t: TemplateListItem) {
    try {
      await restoreTemplate(kind, t.membershipTemplateID);
      setTemplates((prev) => prev.map((x) => (x.membershipTemplateID === t.membershipTemplateID ? { ...x, active: true } : x)));
      toast.success(`${text.Noun} restored`);
    } catch (err) {
      toast.error(getErrorMessage(err, `The ${text.noun[0]} could not be restored.`));
    }
  }

  const narrowed = query !== '' || filtering;
  const columns = columnsFor(kind, locked);

  return (
    <AdminListPage
      title={text.title}
      subtitle={text.subtitle}
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add {text.noun[0]}
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by title or description..."
          searchLabel={`Search ${text.noun[1]}`}
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId={`${text.noun[1]}-show-deleted`}
        >
          <ListFilterBar filters={filters} />
        </AdminListToolbar>
      }
    >
      {loading ? (
        <SpinnerOverlay label={`Loading ${text.noun[1]}…`} />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={narrowed ? `No ${text.noun[1]} found` : `No ${text.noun[1]} yet`}
          hint={narrowed ? 'Try adjusting your search or filters.' : locked ? undefined : `Add a ${text.noun[0]} to get started.`}
        />
      ) : (
        <>
          <AdminListTable columns={columns} aria-label={text.title} sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((t) => {
              const id = t.membershipTemplateID;
              const deleted = !t.active;
              const title = templateTitle(t);
              const a = kind === 'package' ? t.availability : null;
              return (
                <tr key={id} className={adminRowClass({ deleted, clickable: !deleted })} onClick={deleted ? undefined : () => setEditing(id)}>
                  <td className={ADMIN_TD_CLASS}>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100">
                        {t.membershipImage ? (
                          <img src={t.membershipImage} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <Package className="h-4 w-4 text-slate-400" aria-hidden />
                        )}
                      </div>
                      <div className="min-w-0">
                        <AdminRowName deleted={deleted} nameTestId="template-title">
                          {title}
                        </AdminRowName>
                        {a && (
                          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-slate-500" data-testid="package-availability">
                            {a.packageType === 1 && <OutlineBadge tone="purple">Promo</OutlineBadge>}
                            <span>{availabilityParts(a).join(' · ')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'px-1 text-center')}>
                    <FlagIcon on={Boolean(t.membershipImage)} icon={<ImageIcon />} className="text-green-500" label="Has image" />
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'px-1 text-center')}>
                    <FlagIcon on={t.publicStatus} icon={<Store />} className="text-orange-500" label="Available in store" />
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    {descriptionToText(t.description).trim() ? (
                      <span className="block max-w-[240px] truncate text-slate-600" title={descriptionToText(t.description)}>
                        {descriptionToText(t.description)}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'tabular-nums')}>
                    {t.membershipTotal > 0 ? `$${t.membershipTotal.toFixed(2)}` : <span className="text-slate-400">—</span>}
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    {t.productItems.length === 0 && t.procedureItems.length === 0 ? (
                      <span className="text-slate-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        <ItemsPill tone="blue" icon={<Package />} items={t.productItems} heading="Products" label={`Products in ${title}`} />
                        <ItemsPill
                          tone="purple"
                          icon={<Stethoscope />}
                          items={t.procedureItems}
                          heading="Procedures"
                          label={`Procedures in ${title}`}
                        />
                      </div>
                    )}
                  </td>
                  {kind === 'package' && (
                    <td className={ADMIN_TD_CLASS}>
                      {t.categories.length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {t.categories.map((c) => (
                            <OutlineBadge key={c.id} tone="teal" className="text-[10px]">
                              {c.name}
                            </OutlineBadge>
                          ))}
                        </div>
                      )}
                    </td>
                  )}
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => void handleRestore(t)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${title}`} onClick={() => setEditing(id)} />
                            <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => setDeleting(t)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={text.noun} />
        </>
      )}

      {editing !== null && (
        <MembershipTemplateForm
          key={editing}
          kind={kind}
          templateId={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
          onImageChanged={(id, url) =>
            setTemplates((prev) => prev.map((t) => (t.membershipTemplateID === id ? { ...t, membershipImage: url } : t)))
          }
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Confirm delete"
        message={`Are you sure you want to delete this ${text.noun[0]}? You can restore it later.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </AdminListPage>
  );
}

